import type {
  AdSlot,
  LoadRequest,
  SmartclipPluginConfig,
} from "@foundation-player/api";
import { Media, SmartclipConfig } from "./media_player.types";
import { Plugin } from "./media_plugin";
import {
  MEDIA_POSITION_STORAGE_KEY,
  type MediaPositions,
} from "./media_plugin-position";
import { loadResource, storageAvailable } from "./utils";

function createAdCallUrl(
  offset: number,
  targeting: string,
  durationInSeconds: number,
  zone: string,
  userPlus: boolean,
  restricted = false,
  scope?: string,
  adType: "linear" | "non-linear" = "linear",
): string {
  const adCallUrl = new URL("https://ad-ipd.sxp.smartclip.net/select");
  const vdcParameter = new URLSearchParams(targeting);

  adCallUrl.searchParams.set("ang_conlen", durationInSeconds.toString());
  adCallUrl.searchParams.set("ang_ref", window.location.href);
  adCallUrl.searchParams.set(
    "fwd_cf",
    durationInSeconds < 600 ? "short_form" : "long_form",
  );
  adCallUrl.searchParams.set("fwd_vpaid", "1");
  adCallUrl.searchParams.set("s", `ems_${scope}${zone ? "/" + zone : ""}`);
  if (adType === "non-linear") {
    adCallUrl.searchParams.set("sz", "over");
  } else {
    adCallUrl.searchParams.set("sz", offset === 0 ? "pre" : "post");
  }
  adCallUrl.searchParams.set("type", "vast4");

  vdcParameter.forEach((value, key) => {
    adCallUrl.searchParams.set(key, value);
  });

  if (userPlus) {
    adCallUrl.searchParams.set(`fwd_${scope}plus`, "1");
  }

  if (restricted) {
    adCallUrl.searchParams.set("fwd_plus_content", "1");
  }

  let urlString = adCallUrl.href;
  urlString += "&rnd={TIMESTAMP}";
  urlString += "&ang_plrh={PLAYER_HEIGHT}";
  urlString += "&ang_plrw={PLAYER_WIDTH}";
  urlString += "&consent={CONSENT}";
  urlString += "&fwd_adb=[BLOCKED]";

  return urlString;
}

/**
 * For further information on the advertising plugin, please also refer
 * to the corresponding API documentation in the Foundation Player documentation:
 *
 * @see https://docs.player.foundation/v2/api/type/load-request/#plugins
 * @see https://docs.player.foundation/v2/api/type/load-request/#foundationpluginsmartclip
 * @see https://docs.player.foundation/v2/api/type/smartclip-plugin-config/
 */
export class PluginAdvertising extends Plugin {
  readonly #vccUrl =
    "https://bilder-a.akamaihd.net/ip/vcc/vcc.min.js?site=stern";

  readonly #homadClientConfig = {
    alias: "desternadipdsxpsmartclipnet",
    config: "https://hgc-cf-cache-1.svonm.com/www.stern.de/config.json",
    enabled: true,
    server: [
      "https://ssl.1.damoh.aws.stern.de/[hash]/",
      "https://ssl.2.damoh.aws.stern.de/[hash]/",
      "https://ssl.3.damoh.aws.stern.de/[hash]/",
    ],
  };

  public constructor(media: Media) {
    super(media);
  }

  /**
   * Checks zone of MoltenBundle, because it may differ from the zone set by us.
   */
  static #areAdsAllowedByMoltenBundle(): boolean {
    const adZone: string | undefined =
      window.MoltenBundle?.getClient().getZone();
    return adZone !== undefined && adZone !== "_noad";
  }

  public isEnabled(): boolean {
    return !this.media.disableAds;
  }

  public async addFoundationLoadRequestPlugins(): Promise<
    LoadRequest["plugins"] | undefined
  > {
    return new Promise((resolve) => {
      // Add video code to start of MoltenBundle cmd by `unshift` to have it executed first
      window.MoltenBundle?.cmd.unshift(() => {
        if (!PluginAdvertising.#areAdsAllowedByMoltenBundle()) {
          resolve(undefined);
        }
        this.#getFoundationPluginAdvertisingConfig().then(
          (config) => {
            if (config) {
              resolve({ "foundation.plugin.smartclip": config });
            }
          },
          () => {
            resolve(undefined);
          },
        );
      });
      if (typeof window.MoltenBundle.run === "function") {
        window.MoltenBundle.run();
      }
    });
  }

  /**
   * Calculate non-linear ad slots based on video duration
   *
   * Rules:
   * - < 01:30 (90s): No non-linear ads
   * - 01:30 - 06:40 (90s - 400s): Ad at 01:00 (60s)
   * - 06:40 - 08:00 (400s - 480s): Ads at 01:00 (60s) and 06:30 (390s)
   * - 08:00 - 15:10 (480s - 910s): Ad at 05:00 (300s)
   * - 15:10 - 25:10 (910s - 1510s): Ads at 05:00 (300s) and 15:00 (900s)
   * - > 25:10 (1510s): Ads at 05:00 (300s), 15:00 (900s) and 25:00 (1500s)
   */
  #getNonLinearAdOffsets(durationInSeconds: number): number[] {
    const offsets: number[] = [];

    // < 01:30 min: keine non-linear ads
    if (durationInSeconds < 90) {
      return offsets;
    }

    // 01:30 - 06:40 min: Ad bei 01:00
    if (durationInSeconds >= 90 && durationInSeconds < 400) {
      offsets.push(60);
    }

    // 06:40 - 08:00 min: Ads bei 01:00 und 06:30
    if (durationInSeconds >= 400 && durationInSeconds < 480) {
      offsets.push(60, 390);
    }

    // 08:00 - 15:10 min: Ad bei 05:00
    if (durationInSeconds >= 480 && durationInSeconds < 910) {
      offsets.push(300);
    }

    // 15:10 - 25:10 min: Ads bei 05:00 und 15:00
    if (durationInSeconds >= 910 && durationInSeconds < 1510) {
      offsets.push(300, 900);
    }

    // > 25:10 min: Ads bei 05:00, 15:00 und 25:00
    if (durationInSeconds >= 1510) {
      offsets.push(300, 900, 1500);
    }

    return offsets;
  }

  async #getAdSlots(): Promise<AdSlot[]> {
    // Receive targeting parameter once for all possible adslots. This is done here to call
    // Moltens getSXPKeysAsync function only once.

    const adSlots: AdSlot[] = [];

    const setupAds = (targeting: string) => {
      if (
        this.media.durationInSeconds &&
        this.media.durationInSeconds > 30 &&
        !this.media.userNoad &&
        this.media.zone !== "_noad"
      ) {
        let position;
        if (storageAvailable("localStorage") && this.media.id) {
          const data = localStorage.getItem(MEDIA_POSITION_STORAGE_KEY);
          if (data) {
            const positions: MediaPositions = JSON.parse(data);
            position = positions[this.media.id];
          }
        }

        // https://docs.player.foundation/v2/api/type/linear-ad-slot/
        // Don't push a preroll ad if the position of this video has been saved.
        if (!position) {
          adSlots.push({
            type: "linear",
            offsetInSeconds: 0,
            adCall: createAdCallUrl(
              0,
              targeting,
              this.media.durationInSeconds,
              this.media.zone!,
              this.media.userPlus!,
              this.media.restricted,
              this.media.scope,
              "linear",
            ),
          });
        }

        // Postroll ad
        adSlots.push({
          type: "linear",
          offsetInSeconds: -1,
          adCall: createAdCallUrl(
            -1,
            targeting,
            this.media.durationInSeconds,
            this.media.zone!,
            this.media.userPlus!,
            this.media.restricted,
            this.media.scope,
            "linear",
          ),
        });

        // Non-linear ads (overlay ads)
        // https://docs.player.foundation/v2/api/type/non-linear-ad-slot/
        const nonLinearOffsets = this.#getNonLinearAdOffsets(
          this.media.durationInSeconds,
        );

        nonLinearOffsets.forEach((offset) => {
          adSlots.push({
            type: "non-linear",
            offsetInSeconds: offset,
            adCall: createAdCallUrl(
              offset,
              targeting,
              this.media.durationInSeconds!,
              this.media.zone!,
              this.media.userPlus!,
              this.media.restricted,
              this.media.scope,
              "non-linear",
            ),
          });
        });
      }
    };

    const defaultTargetParams = "&fwd_j4=1:2&fwd_j5=2:3:4";

    const getSXPKeysWithTimeout = (): Promise<string> => {
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          resolve(defaultTargetParams);
        }, 3000);

        window.MoltenBundle.Video.getSXPKeysAsync()
          .then((targeting: string) => {
            clearTimeout(timeout);
            resolve(targeting);
          })
          .catch(() => {
            clearTimeout(timeout);
            resolve(defaultTargetParams);
          });
      });
    };

    const handleTargeting = async () => {
      window.MoltenBundle.Video.setTargeting(
        "vcd",
        this.media.durationInSeconds!,
      );
      const targeting = await getSXPKeysWithTimeout();
      setupAds(targeting);
    };

    if (window.MoltenBundle) {
      if (window.MoltenBundle.Video) {
        await handleTargeting();
      } else {
        await new Promise<void>((resolve) => {
          window.addEventListener("AC_ready", async () => {
            await handleTargeting();
            resolve();
          });
        });
      }
    }

    return adSlots;
  }

  #getSmartclipConfig(): SmartclipConfig | undefined {
    if (!window.videoCreativeController) {
      console.error(
        "Foundation Player",
        "window.videoCreativeController script is not available!",
      );
      return undefined;
    }

    const smartclipConfig: SmartclipConfig =
      window.videoCreativeController.getSmartClientCoreAdvertisingConfig();

    // Partially replace homad config from VCC by tenant specific values
    if (smartclipConfig.adReinsertion?.homad) {
      smartclipConfig.adReinsertion.homad.setup.activationCallback = async (
        reason: number,
      ): Promise<void> => {
        return new Promise((resolve) => {
          console.info(
            "Foundation Player",
            "Homad activated by reason: ",
            reason,
          );
          resolve();
        });
      };
      // @ts-expect-error value not assignable to field type string
      smartclipConfig.adReinsertion.homad.setup.clientConfig =
        this.#homadClientConfig;
      smartclipConfig.adReinsertion.homad.setup.globalConfig =
        "https://s3.amazonaws.com/homad-global-configs.schneevonmorgen.com/global_config.json";
    } else {
      // Delete adReinsertion, if homad is disabled by tenant config.
      delete smartclipConfig.adReinsertion;
    }

    return smartclipConfig;
  }

  async #getFoundationPluginAdvertisingConfig(): Promise<
    SmartclipPluginConfig | undefined
  > {
    await loadResource(this.#vccUrl);
    if (!window.videoCreativeController) {
      console.error(
        "Foundation Player",
        "window.videoCreativeController is not available!",
      );
      return undefined;
    }
    const qualitySettings = window.videoCreativeController.getQualitySettings();
    return {
      enabled: true,
      qualitySettings,
      smartclipConfig: this.#getSmartclipConfig(),
      adSlots: await this.#getAdSlots(),
    } as SmartclipPluginConfig;
  }
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    MoltenBundle: any;
    videoCreativeController: any;
  }
}
